// Booking a car (demo case 1): three steps on one page. Time, then the pool's cars in priority
// order with what is free, then confirm. A returning user arrives with the last trip prefilled
// (Boka igen) and only confirms. A new user sees the step guide and the help under each step.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { sv } from '@aldorock-fordon/shared/sv';
import type { AvailableVehicle, BookingView, PoolSummary } from '@aldorock-fordon/shared/api-types';
import { api, ApiError, type Me } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { ErrorSummary, SelectField, TextField } from '../ui/Fields.tsx';
import { Facts } from '../ui/Facts.tsx';
import {
  clock,
  day,
  kr,
  moment,
  span,
  stockholmDate,
  stockholmTime,
  todayIso,
  vehicleName,
} from '../format.ts';

type Driver = { id: string; name: string; department: string | null; costCentre: string | null };

/** 'YYYY-MM-DD' and 'HH:MM' in Stockholm to an ISO instant. */
export function toInstant(date: string, time: string): string {
  const [h, m] = time.split(':').map(Number) as [number, number];
  // Find the UTC instant whose Stockholm wall clock is date time.
  const guess = Date.UTC(
    Number(date.slice(0, 4)),
    Number(date.slice(5, 7)) - 1,
    Number(date.slice(8, 10)),
    h,
    m,
  );
  for (const offset of [60, 120]) {
    const d = new Date(guess - offset * 60_000);
    if (stockholmDate(d.toISOString()) === date && stockholmTime(d.toISOString()) === time)
      return d.toISOString();
  }
  return new Date(guess - 60 * 60_000).toISOString();
}

const nextHour = () => {
  const d = new Date(Date.now() + 3600_000);
  return `${String(Number(stockholmTime(d.toISOString()).slice(0, 2))).padStart(2, '0')}:00`;
};
const plusHours = (time: string, n: number) =>
  `${String(Math.min(23, Number(time.slice(0, 2)) + n)).padStart(2, '0')}:00`;

export function BookPage({ me }: { me: Me }) {
  const h1 = usePage(sv.booking.title);
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const rebook = params.get('rebook');
  const pools = useQuery({ queryKey: ['pools'], queryFn: () => api<PoolSummary[]>('/pools') });
  const bookable = useMemo(() => (pools.data ?? []).filter((p) => p.member), [pools.data]);
  const [chosenPool, setPoolId] = useState(params.get('pool') ?? '');
  // Rebooking repeats the last trip's clock times on the next day, since the stored times have passed.
  const [rebookShift] = useState(() => {
    const from = params.get('from');
    if (!rebook || !from) return 0;
    const fromDate = stockholmDate(from);
    if (fromDate >= todayIso()) return 0;
    const tomorrow = stockholmDate(new Date(Date.now() + 86_400_000).toISOString());
    return Math.round(
      (new Date(`${tomorrow}T12:00:00Z`).getTime() - new Date(`${fromDate}T12:00:00Z`).getTime()) /
        86_400_000,
    );
  });
  const shifted = (iso: string) =>
    stockholmDate(new Date(new Date(iso).getTime() + rebookShift * 86_400_000).toISOString());
  const [date, setDate] = useState(
    () => params.get('date') ?? (params.get('from') ? shifted(params.get('from')!) : todayIso()),
  );
  const [start, setStart] = useState(() =>
    params.get('from') ? stockholmTime(params.get('from')!) : nextHour(),
  );
  const [end, setEnd] = useState(() =>
    params.get('to') ? stockholmTime(params.get('to')!) : plusHours(nextHour(), 2),
  );
  const [endDate, setEndDate] = useState(() =>
    params.get('to') ? shifted(params.get('to')!) : '',
  );
  const [multiDay, setMultiDay] = useState(
    Boolean(
      params.get('to') &&
      stockholmDate(params.get('to')!) !== stockholmDate(params.get('from') ?? params.get('to')!),
    ),
  );
  const [vehicleId, setVehicleId] = useState(rebook ?? params.get('vehicle') ?? '');
  const [purpose, setPurpose] = useState(params.get('purpose') ?? '');
  const [costCentre, setCostCentre] = useState(me.user.costCentre ?? '');
  const [driverMode, setDriverMode] = useState<'self' | 'other'>('self');
  const [driverQuery, setDriverQuery] = useState('');
  const [driver, setDriver] = useState<Driver | null>(null);
  const [step, setStep] = useState<1 | 2 | 3>(rebook ? 3 : 1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState<BookingView | null>(null);
  const firstTime = useQuery({
    queryKey: ['start'],
    queryFn: () => api<{ firstTime: boolean }>('/start'),
  });

  const poolId = chosenPool || (bookable.find((p) => p.isDefault) ?? bookable[0])?.id || '';
  const startsAt = toInstant(date, start);
  const endsAt = toInstant(multiDay && endDate ? endDate : date, end);
  const validTimes = new Date(endsAt) > new Date(startsAt);
  const available = useQuery({
    queryKey: ['available', poolId, startsAt, endsAt],
    queryFn: () =>
      api<AvailableVehicle[]>(
        `/pools/${poolId}/available?from=${encodeURIComponent(startsAt)}&to=${encodeURIComponent(endsAt)}`,
      ),
    enabled: Boolean(poolId) && validTimes && step >= 2,
  });
  const drivers = useQuery({
    queryKey: ['drivers', driverQuery],
    queryFn: () => api<Driver[]>(`/bookings/drivers?q=${encodeURIComponent(driverQuery)}`),
    enabled: driverMode === 'other' && driverQuery.length >= 2,
  });
  const pool = bookable.find((p) => p.id === poolId) ?? null;
  const chosen = available.data?.find((v) => v.vehicleId === vehicleId) ?? null;
  const hours = Math.max(
    1,
    Math.ceil((new Date(endsAt).getTime() - new Date(startsAt).getTime()) / 3600_000),
  );
  const estimate = pool
    ? (() => {
        const p = pool.priceModel;
        const days = Math.floor(hours / 24);
        const rest = hours - days * 24;
        return (
          p.start +
          days * p.perDay +
          (p.perDay ? Math.min(rest * p.perHour, p.perDay) : rest * p.perHour)
        );
      })()
    : 0;

  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/bookings', {
        method: 'POST',
        body: JSON.stringify({
          poolId,
          vehicleId,
          driverUserId: driverMode === 'other' && driver ? driver.id : null,
          startsAt,
          endsAt,
          purpose: purpose.trim() || null,
          costCentre: costCentre.trim() || null,
        }),
      }),
    onSuccess: async (r) => {
      const b = await api<BookingView>(`/bookings/${r.id}`);
      await qc.invalidateQueries({ queryKey: ['start'] });
      setDone(b);
      window.scrollTo(0, 0);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.errors.length) {
        setErrors(Object.fromEntries(err.errors.map((e) => [e.path, e.message])));
        if (err.code === 'taken') setStep(2);
      } else
        setErrors({
          form: err instanceof ApiError && err.message ? err.message : sv.errors.server,
        });
    },
  });

  if (done) {
    return (
      <>
        <PageHead help="book" title={sv.booking.confirmed} titleRef={h1} />
        <p className="notice" data-state="done" role="status">
          {sv.booking.confirmedIntro}
        </p>
        <Facts
          facts={[
            {
              key: 'v',
              label: sv.columns.vehicle,
              value: vehicleName(done.vehicle),
              testId: 'confirm-vehicle',
            },
            {
              key: 'g',
              label: sv.booking.garage,
              value:
                [done.pool.garage, done.pool.address].filter(Boolean).join(', ') || done.pool.name,
            },
            { key: 't', label: sv.columns.time, value: span(done.startsAt, done.endsAt) },
            done.keyCode
              ? {
                  key: 'k',
                  label: sv.booking.keyCode,
                  value: <strong className="code">{done.keyCode}</strong>,
                  testId: 'confirm-code',
                }
              : { key: 'k', label: sv.columns.key, value: sv.booking.keyNone },
            done.keyWindowStart && done.keyWindowEnd
              ? {
                  key: 'w',
                  label: sv.booking.keyWindow('', '').trim() ? sv.columns.key : '',
                  value: sv.booking.keyWindow(
                    moment(done.keyWindowStart),
                    moment(done.keyWindowEnd),
                  ),
                }
              : null,
            done.vehicle.note
              ? { key: 'n', label: sv.columns.note, value: done.vehicle.note }
              : null,
            { key: 'd', label: sv.columns.driver, value: done.driver.name },
            { key: 'c', label: sv.columns.costCentre, value: done.costCentre ?? sv.none },
            {
              key: 'p',
              label: sv.booking.price,
              value: done.priceSek !== null ? kr(done.priceSek) : sv.booking.priceNone,
            },
            { key: 'nr', label: sv.columns.number, value: done.number },
          ]}
        />
        <div className="form-actions">
          <Link className="btn btn-primary" to="/">
            {sv.booking.done}
          </Link>
          <Link
            className="btn"
            to="/boka"
            onClick={() => {
              setDone(null);
              setStep(1);
              setVehicleId('');
            }}
          >
            {sv.booking.another}
          </Link>
        </div>
      </>
    );
  }

  const stepList = (
    <ol className="steps" aria-label={sv.booking.title}>
      {([1, 2, 3] as const).map((n) => (
        <li key={n} aria-current={step === n ? 'step' : undefined}>
          {n === 1
            ? sv.booking.steps.time
            : n === 2
              ? sv.booking.steps.vehicle
              : sv.booking.steps.confirm}
        </li>
      ))}
    </ol>
  );

  return (
    <>
      <PageHead help="book" title={sv.booking.title} titleRef={h1} />
      {stepList}
      {firstTime.data?.firstTime && step === 1 ? (
        <p className="muted">{sv.booking.firstHelp}</p>
      ) : null}
      <ErrorSummary
        errors={Object.entries(errors).map(([k, m]) => ({
          id: k === 'form' ? 'main' : k,
          message: m,
        }))}
      />

      {step === 1 ? (
        <form
          className="form-block"
          aria-labelledby="step-time"
          onSubmit={(e) => {
            e.preventDefault();
            if (!validTimes) {
              setErrors({ endsAt: sv.booking.errors.order });
              return;
            }
            setErrors({});
            setStep(2);
          }}
        >
          <h2 className="form-heading" id="step-time">
            {sv.booking.stepLabel(1, sv.booking.steps.time)}
          </h2>
          {bookable.length > 1 ? (
            <SelectField
              id="poolId"
              label={sv.booking.pool}
              help={sv.booking.poolHelp}
              value={poolId}
              options={bookable.map((p) => ({ value: p.id, label: p.name }))}
              onChange={setPoolId}
            />
          ) : null}
          <TextField
            id="date"
            label={sv.booking.date}
            type="date"
            value={date}
            required
            onChange={setDate}
          />
          <div className="cols cols-2 tight">
            <TextField
              id="startsAt"
              label={sv.booking.startTime}
              type="time"
              value={start}
              required
              error={errors['startsAt']}
              onChange={setStart}
            />
            <TextField
              id="endsAt"
              label={sv.booking.endTime}
              type="time"
              value={end}
              required
              error={errors['endsAt']}
              onChange={setEnd}
            />
          </div>
          <div className="field">
            <label className="choice" htmlFor="multiDay">
              <input
                type="checkbox"
                id="multiDay"
                checked={multiDay}
                onChange={(e) => {
                  setMultiDay(e.target.checked);
                  if (e.target.checked && !endDate) setEndDate(date);
                }}
              />
              <span>{sv.booking.multiDay}</span>
            </label>
          </div>
          {multiDay ? (
            <TextField
              id="endDate"
              label={sv.booking.endDate}
              type="date"
              value={endDate}
              onChange={setEndDate}
            />
          ) : null}
          <p className="muted">
            {validTimes ? sv.booking.duration(hours) : sv.booking.errors.order}
          </p>
          <div className="form-actions">
            <button className="btn btn-primary" type="submit">
              {sv.booking.next}
            </button>
          </div>
        </form>
      ) : null}

      {step === 2 ? (
        <section aria-labelledby="step-vehicle">
          <h2 className="form-heading" id="step-vehicle">
            {sv.booking.stepLabel(2, sv.booking.steps.vehicle)}
          </h2>
          <p className="muted">
            {span(startsAt, endsAt)}
            {pool ? `, ${pool.name}` : ''}
            {'. '}
            {sv.booking.vehicleHelp}
          </p>
          {pool?.adminNote ? <p className="caption">{pool.adminNote}</p> : null}
          {available.isPending ? <p role="status">{sv.app.loading}</p> : null}
          {available.data && available.data.filter((v) => v.available).length === 0 ? (
            <p className="notice" data-state="bad" role="status">
              {sv.booking.noVehicles}
            </p>
          ) : null}
          <ul className="vehicle-list" aria-label={sv.booking.chooseVehicle}>
            {available.data?.map((v) => (
              <li
                key={v.vehicleId}
                className={v.available ? 'vehicle-option' : 'vehicle-option is-taken'}
              >
                <div className="vehicle-main">
                  <span className="vehicle-name">{vehicleName(v)}</span>
                  <span className="caption">
                    {[
                      v.fuel ? sv.fuels[v.fuel] : null,
                      v.seats ? `${v.seats} ${sv.columns.seats.toLowerCase()}` : null,
                      v.recent ? sv.booking.recentVehicle : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  {v.note ? <span className="small">{v.note}</span> : null}
                </div>
                <div className="vehicle-side">
                  {v.available ? (
                    <span className="status" data-state="done">
                      {sv.booking.available}
                    </span>
                  ) : (
                    <span className="status" data-state="none">
                      {v.blockedBy
                        ? sv.booking.takenUntil(
                            clock(v.blockedBy.startsAt),
                            day(v.blockedBy.endsAt) === day(v.blockedBy.startsAt)
                              ? clock(v.blockedBy.endsAt)
                              : moment(v.blockedBy.endsAt),
                          )
                        : sv.booking.taken}
                    </span>
                  )}
                  {v.available ? (
                    <button
                      className="btn btn-primary"
                      type="button"
                      aria-label={`${sv.booking.choose} ${vehicleName(v)}`}
                      onClick={() => {
                        setVehicleId(v.vehicleId);
                        setErrors({});
                        setStep(3);
                        window.scrollTo(0, 0);
                      }}
                    >
                      {sv.booking.choose}
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
          <div className="form-actions">
            <button className="btn" type="button" onClick={() => setStep(1)}>
              {sv.booking.back}
            </button>
          </div>
        </section>
      ) : null}

      {step === 3 ? (
        <form
          className="form-block"
          aria-labelledby="step-confirm"
          onSubmit={(e) => {
            e.preventDefault();
            setErrors({});
            create.mutate();
          }}
        >
          <h2 className="form-heading" id="step-confirm">
            {sv.booking.stepLabel(3, sv.booking.steps.confirm)}
          </h2>
          <Facts
            top={false}
            facts={[
              {
                key: 'v',
                label: sv.columns.vehicle,
                value: chosen
                  ? vehicleName(chosen)
                  : rebook
                    ? (params.get('vehicleName') ?? sv.app.loading)
                    : sv.none,
                testId: 'summary-vehicle',
              },
              { key: 'p', label: sv.booking.pool, value: pool?.name ?? '' },
              { key: 't', label: sv.columns.time, value: span(startsAt, endsAt) },
              {
                key: 'pr',
                label: sv.booking.price,
                value:
                  pool &&
                  (pool.priceModel.perHour || pool.priceModel.start || pool.priceModel.perDay)
                    ? kr(estimate)
                    : sv.booking.priceNone,
              },
            ]}
          />
          {me.permissions['booking.others'] ? (
            <fieldset className="field">
              <legend className="label">{sv.booking.driver}</legend>
              <span className="help">{sv.booking.driverHelp}</span>
              <div className="choices">
                <label className="choice" htmlFor="driver-self">
                  <input
                    type="radio"
                    id="driver-self"
                    name="driver"
                    checked={driverMode === 'self'}
                    onChange={() => setDriverMode('self')}
                  />
                  <span>{sv.booking.driverSelf}</span>
                </label>
                <label className="choice" htmlFor="driver-other">
                  <input
                    type="radio"
                    id="driver-other"
                    name="driver"
                    checked={driverMode === 'other'}
                    onChange={() => setDriverMode('other')}
                  />
                  <span>{sv.booking.driverOther}</span>
                </label>
              </div>
              {driverMode === 'other' ? (
                <>
                  <TextField
                    id="driverUserId"
                    label={sv.booking.driverSearch}
                    value={driverQuery}
                    error={errors['driverUserId']}
                    onChange={(v) => {
                      setDriverQuery(v);
                      setDriver(null);
                    }}
                  />
                  {drivers.data && !driver ? (
                    <ul className="plain driver-hits">
                      {drivers.data.map((d) => (
                        <li key={d.id}>
                          <button
                            className="link-button"
                            type="button"
                            onClick={() => {
                              setDriver(d);
                              setDriverQuery(d.name);
                              if (d.costCentre) setCostCentre(d.costCentre);
                            }}
                          >
                            {d.name}
                            {d.department ? <span className="muted"> · {d.department}</span> : null}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </>
              ) : null}
            </fieldset>
          ) : null}
          <TextField
            id="costCentre"
            label={sv.booking.costCentre}
            help={sv.booking.costCentreHelp}
            value={costCentre}
            error={errors['costCentre']}
            onChange={setCostCentre}
          />
          <TextField
            id="purpose"
            label={sv.booking.purpose}
            help={sv.booking.purposeHelp}
            value={purpose}
            error={errors['purpose']}
            onChange={setPurpose}
          />
          <div className="form-actions">
            <button
              className="btn btn-primary"
              type="submit"
              disabled={create.isPending || !vehicleId}
              aria-busy={create.isPending}
            >
              {sv.booking.confirm}
            </button>
            <button className="btn" type="button" onClick={() => setStep(2)}>
              {sv.bookingPage.changeVehicle}
            </button>
            <button className="btn btn-quiet" type="button" onClick={() => setStep(1)}>
              {sv.bookingPage.changeTime}
            </button>
          </div>
        </form>
      ) : null}
    </>
  );
}
